import { formPdf, finishPdf } from './formPdf';
import { chooseFillMode } from './fillable';
import api from '../api';

// On-screen admission / consent form -> PDF in the agency form look (IVORY header, blue section bars, boxes, checkboxes)
const SKIP = 'button, nav, header, aside, script, style, svg, [data-no-pdf], [role="dialog"], .print\\:hidden';
const NOISE = /^IVORY HOME HEALTH CARE$|\b(DOB|MR#|MRN):\s/i;
const txt = (el) => (el?.innerText || el?.textContent || '').replace(/\s+/g, ' ').trim();
const usDate = (v) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || ''); return m ? `${m[2]}/${m[3]}/${m[1]}` : v; };
const same = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();

const valueOf = (el) => {
  if (el.tagName === 'SELECT') return el.value ? txt(el.selectedOptions[0]) : '';
  return el.type === 'date' ? usDate(el.value) : el.value;
};

// the coloured title bar (h1 + Save/Print buttons) is not part of the form
const titleBar = (root) => {
  let n = root.querySelector('h1');
  for (let i = 0; n && n !== root && i < 5; i += 1, n = n.parentElement) if (n.querySelector('button')) return n;
  return null;
};

export const buildFormPdfFromDom = (root, title, patient = {}, blank = false) => {
  const bar = titleBar(root);
  const visible = (el) => !el.closest(SKIP) && !(bar && bar.contains(el)) && el.getClientRects().length > 0;
  const usedLabels = new Set();
  const labelFor = (el, { reuse = false } = {}) => {
    const fallback = el.getAttribute('aria-label') || el.placeholder || '';
    if (el.id) { const l = root.querySelector(`label[for="${CSS.escape(el.id)}"]`); if (l) return txt(l); }
    const wrap = el.closest('label');
    if (wrap) return txt(wrap);
    for (let node = el, depth = 0; node && node !== root && depth < 3; node = node.parentElement, depth += 1) {
      for (let s = node.previousElementSibling; s; s = s.previousElementSibling) {
        if (s.matches('button') || s.querySelector('button') || (bar && bar.contains(s))) continue;
        if (s.querySelector('input, select, textarea, canvas')) break;
        if (/^H[1-4]$/.test(s.tagName) || s.querySelector('h1, h2, h3, h4')) return fallback || txt(s);
        const t = txt(s);
        if (t && t.length < 90 && !NOISE.test(t)) {
          if (usedLabels.has(s) && !reuse) return fallback || t;
          usedLabels.add(s);
          return t;
        }
      }
    }
    return fallback || el.name || '';
  };

  const f = formPdf(title.toUpperCase(), '', { theme: 'color' });
  f.doc.patientInfo = patient;
  const name = patient.name || [patient.firstName, patient.lastName].filter(Boolean).join(' ');
  f.row([['Patient Name', name, 0.45], ['DOB', usDate(patient.dateOfBirth || ''), 0.2], ['MR#', patient.medicalRecordNumber || patient.mrn || '', 0.35]]);
  let row = [];
  let checks = [];
  let section = title;
  let lastGroup = '';
  const seen = new Set();
  const flushRow = () => { if (row.length) { f.row(row.map(([l, v]) => [l, v, 1 / row.length])); row = []; } };
  const flushChecks = () => { if (checks.length) { f.checks(checks); checks = []; } };
  const flush = () => { flushRow(); flushChecks(); };
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  for (let el = walker.nextNode(); el; el = walker.nextNode()) {
    if (!visible(el)) continue;
    const tag = el.tagName;
    if (/^H[2-4]$/.test(tag)) {
      const t = txt(el);
      if (t && !same(t, title) && !NOISE.test(t)) { flush(); f.space(30); f.section(t); section = t; seen.add(t.toLowerCase()); }
    } else if (tag === 'INPUT' && (el.type === 'checkbox' || el.type === 'radio')) {
      flushRow();
      const l = labelFor(el);
      const holder = (el.closest('label') || el).parentElement;
      const group = l.length <= 14 && holder ? labelFor(holder, { reuse: true }) : '';
      if (group && group !== lastGroup && !same(group, section) && !seen.has(group.toLowerCase())) {
        flushChecks(); f.para(group.slice(0, 160), { bold: true, size: 8.5 }); seen.add(group.toLowerCase());
      }
      lastGroup = group;
      if (!l) continue;
      if (l.length > 70) { flushChecks(); f.checkLong(l, el.checked); } else checks.push([l, el.checked]);
    } else if ((tag === 'INPUT' && !['hidden', 'file', 'button', 'submit'].includes(el.type)) || tag === 'SELECT') {
      flushChecks();
      row.push([labelFor(el).slice(0, 60), valueOf(el)]);
      if (row.length === 3) flushRow();
    } else if (tag === 'TEXTAREA') {
      flush();
      f.row([[labelFor(el).slice(0, 90), el.value, 1, { lines: 3 }]]);
    } else if (tag === 'CANVAS' && el.width > 80) {
      flush();
      const empty = !el.getContext('2d').getImageData(0, 0, el.width, el.height).data.some((v, i) => i % 4 === 3 && v);
      f.signatures([labelFor(el) || 'Signature'], [blank || empty ? {} : { image: el.toDataURL('image/png') }]);
    } else if ((tag === 'P' || tag === 'LI') && !el.querySelector('input, select, textarea') && !el.closest('label')) {
      const t = txt(el);
      if (t.length > 25 && !NOISE.test(t) && !seen.has(t.toLowerCase())) { seen.add(t.toLowerCase()); flush(); f.para(tag === 'LI' ? `- ${t}` : t, { size: 8.5 }); }
    }
  }
  flush();
  return finishPdf(f.doc, title);
};

// Ask Pre-filled / Blank / Filled PDF / Print, then deliver. `patient` may be a chart object or a patient id
export const exportFormPdfFromDom = async (root, title, patient, filename) => {
  if (!root) return;
  const mode = await chooseFillMode();
  if (!mode) return;
  const p = typeof patient === 'string' && patient
    ? await api.get(`/patients/${patient}`).then(r => r.data.patient || r.data).catch(() => ({})) : patient || {};
  const doc = buildFormPdfFromDom(root, title, mode === 'blank' ? {} : p, mode === 'blank');
  doc.fillMode = mode;
  doc.save(filename || `${title.replace(/[^\w]+/g, '_')}.pdf`);
};
